import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

import { Button } from '@elements/button'
import { SelectInput } from '@elements/select-input'
import { TextInput } from '@elements/text-input'
import {
  CALENDAR_MONTHS,
  MONTH_OPTIONS,
  getCalendarDays,
} from '@utils/date-time'

import { SelectInputProps, TextInputProps } from 'types/ui'
import { DayDetail } from 'types/ui/calendar'

export type CalendarProps = {
  date?: string | number | null
  minDate?: number
  maxDate?: number
  onSelect?: (dt?: string | number | null) => void
  bordered?: boolean
}

export function Calendar({
  date: _date = null,
  bordered = true,
  onSelect,
  minDate,
  maxDate,
}: CalendarProps) {
  const date = useMemo(() => (_date ? new Date(_date) : new Date()), [_date])

  const [calendarElement, setCalendarElement] =
    useState<HTMLTableElement | null>()
  const [year, setYear] = useState<number>(date.getFullYear())
  const [month, setMonth] = useState<number>(date.getMonth())
  const focusId = useRef<string | null>(null)

  const selectedTimestamp = date.valueOf()

  const calendarRows = useMemo<DayDetail[][]>(() => {
    const calendarDays = getCalendarDays(year, month)
    const rows: DayDetail[][] = []

    for (let i = 7; i <= calendarDays.length; i += 7) {
      rows.push(calendarDays.slice(i - 7, i))
    }
    return rows
  }, [month, year])

  const isInRange = useCallback(
    (dayDetail: DayDetail) => {
      if (dayDetail.month !== month || dayDetail.year !== year) return false

      if (minDate && dayDetail.timestamp < minDate) return false
      if (maxDate && dayDetail.timestamp > maxDate) return false

      return true
    },
    [maxDate, minDate, month, year]
  )

  const handleDaySelect = useCallback<React.MouseEventHandler>(
    (e) => {
      const buttonElement = e.target as HTMLButtonElement
      const val = buttonElement.getAttribute('data-timestamp')
      onSelect?.(Number(val))
    },
    [onSelect]
  )

  const handleNextMonth = useCallback(() => {
    if (month === 11) {
      setMonth(0)
      setYear(year + 1)
    } else {
      setMonth(month + 1)
    }
  }, [month, year])

  const handlePrevMonth = useCallback(() => {
    if (month === 0) {
      setMonth(11)
      setYear(year - 1)
    } else {
      setMonth(month - 1)
    }
  }, [month, year])

  const handleMonthChange = useCallback<SelectInputProps['onChange']>(
    (val) => val && setMonth(Number(val.id)),
    []
  )

  const handleYearChange = useCallback<TextInputProps<string>['onChange']>(
    (val: string | null | undefined) => {
      if (val === null || val === undefined) return
      setYear(Number(val))
    },
    []
  )

  const handleArrowNavigation = useCallback<React.KeyboardEventHandler>(
    (e) => {
      const keyCode = e.code
      const isArrowKey = [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ].includes(keyCode)
      if (!isArrowKey || focusId.current === null) return

      const focusIdParts = focusId.current.split('-')
      const monthDate = Number(focusIdParts[2])

      let nextDate: number
      switch (keyCode) {
        case 'ArrowLeft':
          nextDate = monthDate - 1
          break
        case 'ArrowRight':
          nextDate = monthDate + 1
          break
        case 'ArrowUp':
          nextDate = monthDate - 7
          break
        case 'ArrowDown':
          nextDate = monthDate + 7
          break
        default:
          return
      }

      const nextDaySelector = `button#day-${month}-${nextDate}`
      const nextDayButton: HTMLButtonElement | null | undefined =
        calendarElement?.querySelector(nextDaySelector)

      if (nextDayButton && isArrowKey) {
        e.preventDefault()
        e.stopPropagation()
        nextDayButton.focus()
      }
    },
    [calendarElement, month]
  )

  const calendarDaysLoaded = calendarRows.length > 0

  useEffect(() => {
    if (
      !calendarDaysLoaded &&
      year !== date.getFullYear() &&
      month !== date.getMonth()
    ) {
      setMonth(date.getMonth())
      setYear(date.getFullYear())
    }
  }, [calendarDaysLoaded, date, month, year])

  return (
    <div className='w-min min-w-[282px] sm:min-w-[354px]'>
      <div
        className={classNames(
          'calendar-header flex items-center justify-between w-full p-2 xs:p-4 dark',
          'bg-stone-800 dark:bg-stone-700 rounded-t-md ',
          { 'border-t-2 border-x-2 border-card': bordered }
        )}
      >
        <div className='flex items-center justify-center max-w-full px-2 space-x-3 rounded-t-md '>
          <div className='flex items-center justify-end w-1/2 space-x-2'>
            <Button.Tertiary
              className='!w-10 !min-w-[40px] !py-1.5 !px-2.5'
              sm
              onClick={handlePrevMonth}
            >
              <ChevronLeftIcon className='w-3 h-3 stroke-2 xs:w-4 xs:h-4' />
            </Button.Tertiary>
            <div className='max-w-[calc(100%-44px)] flex-grow'>
              <SelectInput
                options={MONTH_OPTIONS}
                value={{ id: month.toString(), name: CALENDAR_MONTHS[month] }}
                onChange={handleMonthChange}
              />
            </div>
          </div>
          <div className='flex items-center justify-start w-1/2 space-x-2'>
            <TextInput
              className='focus:!text-white inline-block w-full'
              value={year?.toString()}
              type='number'
              onChange={handleYearChange}
            />
            <Button.Tertiary
              className='!w-10 !min-w-[40px] !py-1.5 !px-2.5'
              sm
              onClick={handleNextMonth}
            >
              <ChevronRightIcon className='w-3 h-3 stroke-2 xs:w-4 xs:h-4' />
            </Button.Tertiary>
          </div>
        </div>
      </div>
      <table
        className={classNames('calendar', {
          bordered,
          'opacity-0': !calendarDaysLoaded,
        })}
        ref={setCalendarElement}
        onKeyDown={handleArrowNavigation}
      >
        <thead>
          <tr>
            <th>Sun</th>
            <th>Mon</th>
            <th>Tue</th>
            <th>Wed</th>
            <th>Thu</th>
            <th>Fri</th>
            <th>Sat</th>
          </tr>
        </thead>
        <tbody className='border-2 border-card'>
          {calendarRows.map((row, rowIdx) => (
            <tr key={`${year}-${month}-${rowIdx}`}>
              {row.map((dayDetail: DayDetail) => (
                <td key={dayDetail.timestamp}>
                  <button
                    role='input'
                    onClick={handleDaySelect}
                    onBlur={() => (focusId.current = null)}
                    onFocus={(e) => (focusId.current = e.target.id)}
                    id={`day-${dayDetail.month}-${dayDetail.day}`}
                    data-timestamp={dayDetail.timestamp}
                    disabled={!isInRange(dayDetail)}
                    className={classNames(
                      'border-none w-full h-full aspect-square cursor-pointer text-sm xs:text-base p-3 xs:p-4 text-center',
                      'flex items-center justify-center rounded-full hover:bg-primary-25/[0.5]',
                      'outline-none ring-2 ring-transparent focus:ring-primary-800 !text-white',
                      'transition delay-100 duration-200 dark:focus:ring-primary-50 dark:disabled:!text-stone-500',
                      'disabled:!text-stone-400 disabled:hover:bg-transparent disabled:cursor-not-allowed',
                      {
                        'bg-primary-400  font-bold hover:bg-primary-600 dark:bg-primary-500 dark:hover:bg-primary-700':
                          dayDetail.timestamp === selectedTimestamp,
                        'bg-transparent':
                          dayDetail.timestamp !== selectedTimestamp,
                      }
                    )}
                  >
                    {dayDetail.day}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
